import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useUser } from "@clerk/clerk-react";

import { PracticeRunner } from "@/components/PracticeRunner";
import { useApi } from "@/lib/api";

interface Template {
  id: string;
  name: string;
  methodology: string;
  description: string;
  estimated_minutes: number;
  schema_json: string;
}

interface TodayResponse {
  template: Template | null;
  streak: number;
  todays_session_id: string | null;
}

interface ActiveHabit {
  anchor: string;
  behavior: string;
  celebration: string;
  started_at: number;
}

interface HabitResp {
  habit: ActiveHabit | null;
  done_today: boolean;
}

interface HabitDoneResp {
  newly: boolean;
  total: number;
  current_streak: number;
  done_today: boolean;
}

interface MeResp {
  profile?: { preferences?: Record<string, string> };
}

const REFLECTION_PROMPTS = [
  "One moment yesterday you actually felt alive — what was it?",
  "What are you avoiding today?",
  "Who's been living rent-free in your head, and why?",
  "If today went well — concretely, what does that look like?",
  "What's one thought that keeps looping?",
  "One small thing from yesterday you're quietly proud of.",
  "Where in your body do you feel today most?",
  "What's the version of you you want to be more of, this week?",
  "When did you feel most yourself in the last day or two?",
  "What's the thing you keep meaning to say to yourself?",
];

export default function TodayPage() {
  const api = useApi();
  const nav = useNavigate();
  const { user } = useUser();
  const [data, setData] = useState<TodayResponse | null>(null);
  const [habit, setHabit] = useState<HabitResp | null>(null);
  const [moodPicked, setMoodPicked] = useState<string | null>(null);
  const [reflection, setReflection] = useState("");
  const [reflectionSaved, setReflectionSaved] = useState(false);
  const [runnerOpen, setRunnerOpen] = useState(false);
  const [milestoneToast, setMilestoneToast] = useState<string | null>(null);

  const refreshDay = async () => {
    const [r, h] = await Promise.all([
      api<TodayResponse>("/api/practice/today"),
      api<HabitResp>("/api/me/habit").catch(() => ({ habit: null, done_today: false })),
    ]);
    setData(r);
    setHabit(h);
  };

  useEffect(() => {
    // First time hitting /today: bounce new users to /onboarding so they
    // pick values + design a first tiny habit before seeing the ritual.
    api<MeResp>("/api/me")
      .then((r) => {
        if (!r.profile?.preferences?.values) {
          nav("/onboarding", { replace: true });
          return;
        }
        refreshDay();
      })
      .catch(() => {
        refreshDay();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  // Pick a reflection prompt deterministically per UTC day so the user gets
  // the same one all day. Different across days.
  const reflectionPrompt = useMemo(() => {
    const dayIndex = Math.floor(Date.now() / 86400_000);
    return REFLECTION_PROMPTS[dayIndex % REFLECTION_PROMPTS.length] ?? REFLECTION_PROMPTS[0];
  }, []);

  async function pickMood(label: string, value: number) {
    setMoodPicked(label);
    await api("/api/me/mood", {
      method: "POST",
      body: JSON.stringify({ value, label }),
    }).catch(() => {});
  }

  async function saveReflection() {
    const txt = reflection.trim();
    if (txt.length < 4) return;
    await api("/api/me/journal", {
      method: "POST",
      body: JSON.stringify({ content: txt, kind: "event" }),
    });
    setReflectionSaved(true);
  }

  async function tapHabitDone() {
    const r = await api<HabitDoneResp>("/api/me/habit/done", { method: "POST" });
    setHabit((cur) => (cur ? { ...cur, done_today: true } : cur));
    refreshDay();
    if (r.newly) {
      const milestone = milestoneCopy(r.current_streak);
      if (milestone) {
        setMilestoneToast(milestone);
        setTimeout(() => setMilestoneToast(null), 4500);
      }
    }
  }

  if (!data) {
    return <div className="grid h-full place-items-center text-neutral-400">Loading…</div>;
  }

  const greeting = greet(user?.firstName);
  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="mx-auto max-w-xl px-4 py-6">
      <header className="mb-8">
        <div className="flex items-baseline justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">{greeting}.</h1>
            <p className="mt-1 text-xs text-neutral-500">{today}</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-neutral-500">
            <Link to="/journey" className="hover:text-neutral-900">Journey</Link>
            <Link to="/me" className="hover:text-neutral-900">You</Link>
            <Link to="/coach" className="hover:text-neutral-900">Chat</Link>
          </div>
        </div>
        {data.streak > 0 && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs text-amber-800">
            🔥 {data.streak}-day streak
            <span className="text-amber-500">·</span>
            <span className="text-amber-600">show up, that's enough</span>
          </div>
        )}
      </header>

      {/* Step 0: tiny habit closure — only shown if onboarding declared one */}
      {habit?.habit && (
        <Card label="① Tiny habit">
          <div className="rounded-xl bg-neutral-50 px-3 py-3 text-sm leading-relaxed">
            <span className="text-neutral-500">After I </span>
            <span className="font-medium">{habit.habit.anchor}</span>
            <span className="text-neutral-500">, I will </span>
            <span className="font-medium">{habit.habit.behavior}</span>
            <span className="text-neutral-500">. Then </span>
            <span className="font-medium">{habit.habit.celebration}</span>
            <span className="text-neutral-500">.</span>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <Link to="/me" className="text-[11px] text-neutral-400 hover:text-neutral-700">
              edit
            </Link>
            {habit.done_today ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1.5 text-xs text-emerald-700">
                ✓ Done today
              </span>
            ) : (
              <button
                onClick={tapHabitDone}
                className="rounded-2xl bg-neutral-900 px-4 py-2 text-sm font-medium text-white"
              >
                Did it ✓
              </button>
            )}
          </div>
        </Card>
      )}

      {/* Step 1: mood */}
      <Card label={habit?.habit ? "② Vibe check" : "① Vibe check"}>
        <p className="mb-3 text-sm text-neutral-600">How's it actually going?</p>
        <div className="grid grid-cols-5 gap-2">
          {MOODS.map((m) => {
            const isPicked = moodPicked === m.label;
            const locked = moodPicked !== null;
            return (
              <button
                key={m.label}
                disabled={locked}
                onClick={() => pickMood(m.label, m.value)}
                className={
                  "rounded-2xl border p-2 text-center text-xs transition disabled:cursor-not-allowed " +
                  (isPicked
                    ? "border-neutral-900 bg-neutral-900 text-white"
                    : locked
                      ? "border-neutral-200 bg-neutral-50 text-neutral-400"
                      : "border-neutral-300 bg-white hover:border-neutral-500")
                }
              >
                <div className="text-2xl">{m.emoji}</div>
                <div className="mt-1 leading-tight">{m.text}</div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Step 2: reflection */}
      <Card label={habit?.habit ? "③ One thought" : "② One thought"}>
        <p className="mb-3 text-sm">{reflectionPrompt}</p>
        {reflectionSaved ? (
          <p className="rounded-xl bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
            Got it. Saved.
          </p>
        ) : (
          <>
            <textarea
              rows={3}
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              placeholder="A sentence or two — no need to make it pretty."
              maxLength={280}
              className="w-full resize-none rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-neutral-400"
            />
            <div className="mt-2 flex items-center justify-end gap-2">
              <button
                onClick={() => setReflectionSaved(true)}
                className="rounded-xl px-3 py-1.5 text-xs text-neutral-500 hover:text-neutral-900"
              >
                Skip
              </button>
              <button
                onClick={saveReflection}
                disabled={reflection.trim().length < 4}
                className="rounded-xl bg-neutral-900 px-3 py-1.5 text-xs text-white disabled:opacity-30"
              >
                Save
              </button>
            </div>
          </>
        )}
      </Card>

      {/* Step 3: practice */}
      <Card label={habit?.habit ? "④ Today's set" : "③ Today's set"}>
        {data.todays_session_id ? (
          <div className="rounded-xl bg-emerald-50 px-3 py-3 text-sm text-emerald-700">
            ✓ You're done for today. Want another go?{" "}
            {data.template && (
              <button
                onClick={() => setRunnerOpen(true)}
                className="ml-1 underline hover:text-emerald-900"
              >
                {data.template.name}
              </button>
            )}
          </div>
        ) : data.template ? (
          runnerOpen ? (
            <PracticeRunner
              template={data.template}
              onClose={() => {
                setRunnerOpen(false);
                refreshDay();
              }}
            />
          ) : (
            <div>
              <div className="mb-1 flex items-baseline justify-between">
                <div className="text-base font-medium">{data.template.name}</div>
                <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] uppercase tracking-wider text-neutral-600">
                  {data.template.methodology}
                </span>
              </div>
              <p className="mb-3 text-sm text-neutral-600">{data.template.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-neutral-400">
                  ~{data.template.estimated_minutes} min
                </span>
                <button
                  onClick={() => setRunnerOpen(true)}
                  className="rounded-2xl bg-neutral-900 px-4 py-2 text-sm font-medium text-white"
                >
                  Start →
                </button>
              </div>
            </div>
          )
        ) : (
          <p className="text-sm text-neutral-400">No practice available right now.</p>
        )}
      </Card>

      {/* Floating "talk to Cogi" */}
      <button
        onClick={() => nav("/coach")}
        className="fixed bottom-6 right-6 rounded-full bg-neutral-900 px-4 py-3 text-sm font-medium text-white shadow-lg hover:bg-neutral-700"
        aria-label="Open chat with Cogi"
      >
        💬 Tell Cogi
      </button>

      {/* Milestone toast — fires on habit-tap at 3/7/14/30 day streaks */}
      {milestoneToast && (
        <div
          role="status"
          className="fixed left-1/2 top-6 z-50 -translate-x-1/2 rounded-2xl bg-neutral-900 px-5 py-3 text-sm font-medium text-white shadow-xl"
        >
          {milestoneToast}
        </div>
      )}
    </div>
  );
}

/**
 * Identity-style milestone copy on habit completion. We tie back to the
 * BJ Fogg + Atomic Habits framing: "you're someone who shows up". Returns
 * null on non-milestone days — most days the tap is celebrated only by the
 * checkmark state change.
 */
function milestoneCopy(streak: number): string | null {
  switch (streak) {
    case 3:
      return "3 days. You're someone who shows up.";
    case 7:
      return "A full week. That's a habit forming.";
    case 14:
      return "Two weeks. This is who you're becoming.";
    case 30:
      return "30 days. You did the thing nobody sees.";
    case 60:
      return "60 days. Quietly impressive.";
    case 100:
      return "100 days. You're not the same person who started.";
    default:
      return null;
  }
}

const MOODS = [
  { emoji: "😌", text: "calm", label: "😌 calm", value: 1 },
  { emoji: "🙂", text: "fine", label: "🙂 fine", value: 2 },
  { emoji: "😐", text: "flat", label: "😐 flat", value: 3 },
  { emoji: "😣", text: "tense", label: "😣 tense", value: 4 },
  { emoji: "🥲", text: "off", label: "🥲 off", value: 5 },
];

function greet(name: string | null | undefined): string {
  const base =
    new Date().getHours() < 5
      ? "Late night"
      : new Date().getHours() < 12
        ? "Good morning"
        : new Date().getHours() < 18
          ? "Good afternoon"
          : "Good evening";
  return name ? `${base}, ${name}` : base;
}

function Card({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="mb-4 rounded-2xl bg-white p-4 shadow-sm">
      <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-neutral-400">{label}</div>
      {children}
    </section>
  );
}
